import { useEffect, UIEvent } from 'react';

import { Flex, FloraButton, Input } from '@grupoboticario/flora-react';
import { SmartphoneIcon } from '@grupoboticario/flora-react-icons';
import Cookies from 'js-cookie';
import { SubmitHandler } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router-dom';
import slugify from 'slugify';

import { MessageFormError } from 'components/Drawers/components';
import { ResponsiveFloraTypography } from 'components/ResponsiveFloraTypography';
import { useChangePhoneForm, useUpdateChangeCadastral, useTracking } from 'hooks';
import { ACCESS_TOKEN_KEY_CADASTRAL_CHANGE } from 'services/auth';
import { useRegistrationControllerContext } from 'store';

import StylePhone from './PhoneForm.styles';
import Styles from '../../../../Drawers.styles';
import { MessagePrivacyPolicies } from '../Messages';

import type { AnalyticsType } from 'domains/analytics.d';
import type { ChangeCadastralResponse, RequestChangeCadastralType } from 'domains/change-cadastral';

export const PhoneForm = () => {
  const { sendDataToGTM } = useTracking();
  const navigate = useNavigate();
  const hasChangeToken = Cookies.get(ACCESS_TOKEN_KEY_CADASTRAL_CHANGE);
  const { steps, handleForm, handleError } = useRegistrationControllerContext();
  const { canSubmit, toggleSubmit, handleSubmit, register, isInvalidForm, isMsgPrivacyPolicies } =
    useChangePhoneForm();

  const {
    data,
    isSuccess,
    isLoading,
    isError,
    error: updateRegistrationError,
    mutateAsync: changeCadastral,
  } = useUpdateChangeCadastral();

  const {
    data: dataRegistration,
    detail,
    errorId,
    userMessage,
    success: isSuccessRegistration,
  } = (data as unknown as ChangeCadastralResponse) || {};

  const { sent: isSent } = dataRegistration || {};

  const onSubmit: SubmitHandler<RequestChangeCadastralType> = async (inputData) => {
    await changeCadastral({
      phone: inputData.phone?.replace(/\D+/g, '') ?? '',
    });
  };

  useEffect(() => {
    if (!hasChangeToken) {
      navigate('/perfil', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChangeToken]);

  useEffect(() => {
    if (isLoading) {
      toggleSubmit(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    if (isError || (isSuccess && !isSuccessRegistration && (!isSent || dataRegistration))) {
      handleCallback('erro');

      const errorMessage =
        userMessage?.errorTitle ??
        updateRegistrationError?.message ??
        dataRegistration?.message ??
        (updateRegistrationError as any)?.data?.errorId;

      handleError({
        detail,
        errorId,
        userMessage,
        updateRegistrationError: errorMessage,
        errorMessage,
        isGenericError: isError || (dataRegistration && !isSent && !isSuccessRegistration),
        isBusinessError: isSuccess && !isSuccessRegistration && !dataRegistration,
      });

      navigate('/perfil/editar/celular/erro', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isSuccessRegistration, isSuccess, isSent]);

  useEffect(() => {
    if (isSuccessRegistration && !isLoading) {
      handleForm(true);
      handleCallback('sucesso');
      navigate('/perfil/editar/celular/sucesso', { replace: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessRegistration, isLoading]);

  const trackNewPhoneInput = (event: UIEvent) => {
    const target = event.target as HTMLInputElement;
    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: 'interaction_alterar_celular',
        cd_interaction_detail: target.dataset.title ?? '',
      },
    });
  };

  const handleCallback = (status: 'erro' | 'sucesso') => {
    const errorMessage =
      errorId ??
      userMessage?.errorTitle ??
      updateRegistrationError?.message ??
      dataRegistration?.message ??
      (updateRegistrationError as any)?.data?.errorId;
    const analyticsData: AnalyticsType = {
      event: 'analytics-event',
      event_parameters: {
        event_name: `callback_alteracao_celular_${status}`,
        ...(status === 'erro' && {
          cd_error_message: slugify(errorMessage ?? '', {
            lower: true,
            replacement: '-',
          }),
        }),
      },
    };

    sendDataToGTM(analyticsData);
  };

  if (!steps.start || !steps.validate) {
    return <Navigate to='/perfil' replace />;
  }

  return (
    <Styles.DrawerSection direction='column' gap='$2'>
      <ResponsiveFloraTypography
        fontSize='bodySmallShort'
        fontWeight='bold'
        color='$backgroundAltSecondary'
      >
        Insira o seu novo número de celular nos campos abaixo.
      </ResponsiveFloraTypography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <StylePhone.Wrapper direction='column'>
          <Flex direction='column' gap='$2'>
            <ResponsiveFloraTypography fontSize='bodySmallStandard'>
              <Input
                autoComplete='none'
                id='phone'
                labelText='Novo número de celular:'
                mask='(xx) xxxxx-xxxx'
                optionalText=''
                invalidText=''
                invalid={isInvalidForm}
                disabled={isLoading}
                required
                data-title='input:novo-numero-celular'
                data-eventname='interaction_celular_form'
                aria-label='Novo número de celular'
                {...register('phone', { required: true, onBlur: trackNewPhoneInput })}
              />
            </ResponsiveFloraTypography>
          </Flex>
          <Flex direction='column' gap='$2'>
            <ResponsiveFloraTypography fontSize='bodySmallStandard'>
              <Input
                autoComplete='none'
                id='phoneConfirmation'
                labelText='Confirme novo número de celular:'
                mask='(xx) xxxxx-xxxx'
                optionalText=''
                invalidText=''
                invalid={isInvalidForm}
                disabled={isLoading}
                required
                data-title='input:confirmar-novo-numero-celular'
                data-eventname='interaction_celular_confirmacao_form'
                aria-label='Confirme novo número de celular'
                {...register('phoneConfirmation', {
                  required: true,
                  onBlur: trackNewPhoneInput,
                })}
              />
            </ResponsiveFloraTypography>
          </Flex>
          {isInvalidForm && (
            <MessageFormError message='As informações dos campos estão divergentes.' />
          )}
          {isMsgPrivacyPolicies && <MessagePrivacyPolicies />}
        </StylePhone.Wrapper>
        <Flex direction='column'>
          <ResponsiveFloraTypography fontSize='bodyLargeShort'>
            <FloraButton
              autotagging-flora='on'
              data-flora='button'
              data-flora-text='solicitar-alteracao-de-celular'
              data-autotagging-flora-section='alterar_celular'
              type='submit'
              isFull
              disabled={!canSubmit}
              isLoading={isLoading}
              has='iconLeft'
              icon={<SmartphoneIcon size='16' aria-hidden />}
            >
              Solicitar alteração de celular
            </FloraButton>
          </ResponsiveFloraTypography>
        </Flex>
      </form>
    </Styles.DrawerSection>
  );
};
export default PhoneForm;
